/**
 * Prefetch and Lazy Loading Script
 * Handles lazy loading of images, scripts, and styles while allowing
 * third-party scripts (Meta Business AI, etc.) to load normally
 */

(function () {
  "use strict";

  // Configuration
  const config = {
    imageCount: 0,
    lazyImageThreshold: 20, // First 20 images load normally, rest are lazy
    isPSA: window.__isPSA || false,
  };

  // List of external domains/scripts that should NOT be modified
  const externalScriptPatterns = [
    "facebook.com",
    "meta.com",
    "fbcdn.net",
    "connect.facebook.net",
    "acsbapp.com",
    "googletagmanager.com",
    "google-analytics.com",
    "klaviyo.com",
    "cdnjs.cloudflare.com",
    "code.jquery.com",
    "cdn.jsdelivr.net",
  ];

  // CSS files to lazy load
  const lazy_css = [];
  // JS files to lazy load
  const lazy_js = [];

  /**
   * Check if a script src should be excluded from modification
   */
  function shouldExcludeScript(src) {
    if (!src) return false;
    return externalScriptPatterns.some((pattern) => src.includes(pattern));
  }

  /**
   * Handle IFRAME elements - set data-src to src
   */
  function handleIframe(iframe) {
    const dataSrc = iframe.getAttribute("data-src");
    if (dataSrc) {
      iframe.setAttribute("loading", "lazy");
      iframe.setAttribute("data-src", iframe.src);
      iframe.removeAttribute("src");
    }
  }

  /**
   * Handle IMG elements - lazy load after threshold
   */
  function handleImage(img) {
    config.imageCount++;

    if (config.isPSA) {
      // For PSA mode, convert src to data-src
      const src = img.getAttribute("src");
      if (src) {
        img.setAttribute("loading", "lazy");
        img.setAttribute("data-src", src);
        img.removeAttribute("src");
      }
    } else {
      // Only lazy load images after the threshold
      if (config.imageCount > config.lazyImageThreshold) {
        img.setAttribute("loading", "lazy");
      }
    }
  }

  /**
   * Handle LINK elements - lazy load matching CSS
   */
  function handleLink(link) {
    if (link.tagName !== "LINK") return;

    const href = link.getAttribute("href");
    if (!href) return;

    // Check if this CSS should be lazy loaded
    if (lazy_css.length > 0) {
      lazy_css.forEach((cssPattern) => {
        if (href.includes(cssPattern)) {
          link.setAttribute("data-href", href);
          link.removeAttribute("href");
        }
      });
    }
  }

  /**
   * Handle SCRIPT elements - lazy load matching scripts
   * IMPORTANT: Do NOT modify external third-party scripts
   */
  function handleScript(script) {
    const src = script.getAttribute("src");

    // NEVER modify external scripts (Meta Business AI, Facebook, etc.)
    if (src && shouldExcludeScript(src)) {
      return; // Let external scripts load normally
    }

    // Handle scripts with data-src attribute (internal scripts only)
    const dataSrc = script.getAttribute("data-src");
    if (dataSrc && !shouldExcludeScript(dataSrc)) {
      script.setAttribute("data-src", src || dataSrc);
      script.removeAttribute("src");
      script.type = "text/lazyload";
    }

    // Check if script should be lazy loaded based on patterns
    if (src && lazy_js.length > 0) {
      lazy_js.forEach((jsPattern) => {
        if (src.includes(jsPattern) && !shouldExcludeScript(src)) {
          script.setAttribute("data-src", src);
          script.removeAttribute("src");
        }
      });
    }

    // Handle inline scripts with specific patterns (but not external scripts)
    const scriptContent = script.innerHTML;
    if (scriptContent && !src) {
      // Only modify if it contains PreviewBarInjector patterns
      if (scriptContent.includes("if(window.attachEvent)")) {
        script.innerHTML = scriptContent
          .replace(
            "if(window.attachEvent)",
            "document.addEventListener('asyncLazyLoad',function(event){asyncLoad();});if(window.attachEvent)"
          )
          .replaceAll(", asyncLoad", ", function(){}");
      }

      // Handle PreviewBarInjector patterns
      if (
        scriptContent.includes("PreviewBarInjector") ||
        scriptContent.includes("adminBarInjector")
      ) {
        script.innerHTML = scriptContent.replace(
          "if(window.attachEvent)",
          "asyncLoad"
        );
      }
    }
  }

  /**
   * MutationObserver callback to handle dynamically added elements
   */
  function handleMutations(mutations) {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        // Only process element nodes
        if (node.nodeType !== 1) return;

        const tagName = node.tagName;

        // Handle different element types
        switch (tagName) {
          case "IFRAME":
            handleIframe(node);
            break;

          case "IMG":
            handleImage(node);
            break;

          case "LINK":
            handleLink(node);
            break;

          case "SCRIPT":
            handleScript(node);
            break;
        }
      });
    });
  }

  // Create and start the MutationObserver
  const observer = new MutationObserver(handleMutations);

  // Observe the entire document
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Expose disconnect method globally if needed
  if (typeof window.uLTS === "undefined") {
    window.uLTS = {
      disconnect: () => observer.disconnect(),
    };
  }
})();
